import { useState } from "react";
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LeafModal } from "../../leaf/LeafModal/LeafModal";
import { IconCopy } from "../../icons/IconCopy/IconCopy";
import { IconActionPhone } from "../../icons/IconActionPhone/IconActionPhone";
import { IconActionMessage } from "../../icons/IconActionMessage/IconActionMessage";
import { IconWhatsAppOutline } from "../../icons/IconWhatsAppOutline/IconWhatsAppOutline";
import commonContent from "../../../content/common";
import { useIsMobile } from "../../../components/ActionReports/hooks/useIsMobile";
import "./ContactLink.scss";

type ContactMethod = "call" | "text" | "whatsApp" | "email";

type ContactLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  method: ContactMethod;
  /** Phone number or email address shown (and copied) in the confirmation modal */
  contact?: string;
};

const EmailGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M3 3.5h12A1.5 1.5 0 0 1 16.5 5v8a1.5 1.5 0 0 1-1.5 1.5H3A1.5 1.5 0 0 1 1.5 13V5A1.5 1.5 0 0 1 3 3.5Zm0 1.5v.3l6 4.2 6-4.2V5H3Zm12 2.14-5.57 3.9a.75.75 0 0 1-.86 0L3 7.14V13h12V7.14Z"
      fill="currentColor"
    />
  </svg>
);

const METHOD_ICON: Record<ContactMethod, ReactNode> = {
  call: <IconActionPhone />,
  text: <IconActionMessage />,
  whatsApp: <IconWhatsAppOutline />,
  email: <EmailGlyph />,
};

/**
 * Drop-in replacement for the call / text / WhatsApp / email anchors.
 * On mobile viewports a tap opens a confirmation modal first (ABOT-14033); the native
 * link is only followed from the modal's action anchor. On larger viewports it is a plain link.
 */
export const ContactLink = ({
  method,
  contact = "",
  onClick,
  href,
  target,
  rel,
  children,
  ...anchorProps
}: ContactLinkProps) => {
  const { t } = useTranslation(["common"]);
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!isMobile) {
      onClick?.(e);
      return;
    }
    e.preventDefault();
    setOpen(true);
  };

  const close = () => setOpen(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(contact).catch(() => {});
    close();
  };

  const defaults = commonContent.contactConfirmation;

  return (
    <>
      <a {...anchorProps} href={href} target={target} rel={rel} onClick={handleClick}>
        {children}
      </a>
      {isMobile && (
        <LeafModal
          open={open}
          onCloseCallbackFn={close}
          id="contact-confirmation"
          backdropStyle="frosted"
          title={t("contactConfirmation.title", defaults.title as string)}
        >
          <div className="contact-confirmation__content" data-testid="contact-confirmation-modal">
            <p className="contact-confirmation__contact" data-testid="contact-confirmation-contact">
              {contact}
            </p>
            <div className="contact-confirmation__actions">
              <button
                className="contact-confirmation__button contact-confirmation__button--copy"
                onClick={handleCopy}
                type="button"
                data-testid="contact-confirmation-copy"
              >
                <IconCopy />
                {t("contactConfirmation.copy", defaults.copy as string)}
              </button>
              <a
                className="contact-confirmation__button contact-confirmation__button--action"
                href={href}
                target={target}
                rel={rel}
                data-testid="contact-confirmation-confirm"
                onClick={(e) => {
                  onClick?.(e);
                  close();
                }}
              >
                {METHOD_ICON[method]}
                {t(`contactConfirmation.${method}`, defaults[method] as string)}
              </a>
            </div>
          </div>
        </LeafModal>
      )}
    </>
  );
};
